import { useCallback, useEffect, useRef } from 'react'
import { useUIStore } from '../stores/useUIStore'

export const useFileHandling = (maxFiles = 5) => {
  const chatInputRef = useRef(null)
  const fileInputRef = useRef(null)
  const dropZoneRef = useRef(null)

  const {
    addFiles,
    showSnackbar,
    removeFile: storeRemoveFile,
    setIsDragging,
  } = useUIStore()

  const handleFileChange = useCallback(
    event => {
      const files = Array.from(event.target.files || [])
      if (files.length === 0) return

      const currentFiles = useUIStore.getState().selectedFiles
      const newFiles = files.filter(
        file => !currentFiles.some(sf => sf.name === file.name && sf.size === file.size)
      )

      const totalFiles = currentFiles.length + newFiles.length

      if (totalFiles > maxFiles) {
        const allowed = maxFiles - currentFiles.length
        if (allowed > 0) {
          const acceptedFiles = newFiles.slice(0, allowed)
          addFiles(acceptedFiles)
          showSnackbar(`Massimo ${maxFiles} file consentiti.`)
        } else {
          showSnackbar(`Hai già raggiunto il limite di ${maxFiles} file.`)
        }
      } else {
        addFiles(newFiles)
      }
      event.target.value = null
    },
    [addFiles, maxFiles, showSnackbar]
  )

  const removeFile = useCallback(index => storeRemoveFile(index), [storeRemoveFile])

  useEffect(() => {
    const dropZone = dropZoneRef.current
    if (!dropZone) return

    const handleDragOver = e => {
      e.preventDefault()
      e.stopPropagation()
      if (!useUIStore.getState().isDragging) setIsDragging(true)
    }

    const handleDragLeave = e => {
      e.preventDefault()
      e.stopPropagation()
      if (e.currentTarget.contains(e.relatedTarget)) return
      setIsDragging(false)
    }

    const handleDrop = e => {
      e.preventDefault()
      e.stopPropagation()
      setIsDragging(false)

      const droppedFiles = Array.from(e.dataTransfer.files)
      const validTypes = [
        'image/png',
        'image/jpeg',
        'image/webp',
        'image/jpg',
        'application/pdf',
        'text/plain',
        'text/markdown',
        'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
        'application/msword',
        'text/csv',
        'application/json',
        'text/xml',
      ]

      const validFiles = droppedFiles.filter(
        f =>
          validTypes.some(type => f.type.match(type)) ||
          f.name.match(/\.(md|json|xml|csv|txt)$/i)
      )

      if (validFiles.length > 0) {
        const currentFiles = useUIStore.getState().selectedFiles
        const newFilesToAdd = validFiles.filter(
          file =>
            !currentFiles.some(sf => sf.name === file.name && sf.size === file.size)
        )
        addFiles(newFilesToAdd.slice(0, maxFiles - currentFiles.length))
      }

      if (fileInputRef.current) fileInputRef.current.value = null
    }

    dropZone.addEventListener('dragover', handleDragOver)
    dropZone.addEventListener('dragleave', handleDragLeave)
    dropZone.addEventListener('drop', handleDrop)

    return () => {
      dropZone.removeEventListener('dragover', handleDragOver)
      dropZone.removeEventListener('dragleave', handleDragLeave)
      dropZone.removeEventListener('drop', handleDrop)
    }
  }, [addFiles, maxFiles, setIsDragging, showSnackbar])

  useEffect(() => {
    const chatInput = chatInputRef.current
    if (!chatInput) return

    const handlePaste = event => {
      const items = event.clipboardData.items
      const filesToUpload = []
      for (let i = 0; i < items.length; i++) {
        if (items[i].kind === 'file') {
          const file = items[i].getAsFile()
          if (file) filesToUpload.push(file)
        }
      }

      if (filesToUpload.length > 0) {
        event.preventDefault()
        addFiles(filesToUpload)
      }
    }

    chatInput.addEventListener('paste', handlePaste)
    return () => chatInput.removeEventListener('paste', handlePaste)
  }, [addFiles, maxFiles, showSnackbar])

  return {
    chatInputRef,
    fileInputRef,
    dropZoneRef,
    handleFileChange,
    removeFile,
  }
}
